// src/features/system/developer/components/video-gallery/VideoGridView.tsx
// ────────────────────────────────────────────────────────────────────────────
// Gallery (grid) view of the Videos surface — YouTube/IG-grid style.
//
// Rendering contract:
//   • Day-grouped sections (header + items), newest day first, items inside
//     each day chronological. Reuses `groupByDay()` so the grid stays in
//     parity with PACMobile's video list and any future surface.
//   • Responsive CSS grid: 2 cols mobile → 6 cols on 2xl. Cards keep their
//     16:9 poster aspect via `VideoCard`'s `aspect-video` poster.
//   • Sticky day headers so users always know which date they're scrolling
//     through (YT Studio + Google Photos idiom).
//   • Tap a card to open the existing `VideoPreviewModal` — DRY, no second
//     player implementation. Modal owns its own data fetch lifecycle.
//   • Trash button on each card delegates to the panel's confirm-delete
//     modal (same flow as the feed rail).
//
// Why a sibling component (not a flag inside VideoFeed):
//   The feed is fundamentally a vertical-snap player; the grid is a static
//   browser. Their interaction models, perf budgets, and DOM shapes don't
//   overlap. Keeping them split honours SRP and lets each evolve.
// ────────────────────────────────────────────────────────────────────────────
import React, { useState } from 'react';
import VideoCard from './VideoCard';
import VideoPreviewModal from './VideoPreviewModal';
import { groupByDay } from './groupByDay';
import { formatBytes, formatDuration } from './format';
import type { VideoFeedItem } from './types';
import type { UseVideoGalleryReturn } from './useVideoGallery';

interface Props {
  v: UseVideoGalleryReturn;
  onRequestDelete: (item: VideoFeedItem) => void;
  /** Hides destructive card affordances (delete). */
  readOnly?: boolean;
}

// ── Date label helpers ───────────────────────────────────────────────────

function dayHeaderLabel(dayStartMs: number): string {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
  const oneDay = 24 * 60 * 60 * 1000;

  if (dayStartMs === today)            return 'Today';
  if (dayStartMs === today - oneDay)   return 'Yesterday';

  const d = new Date(dayStartMs);
  const yearChunk = d.getFullYear() === now.getFullYear() ? '' : `, ${d.getFullYear()}`;
  return `${d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}${yearChunk}`;
}

export default function VideoGridView({ v, onRequestDelete, readOnly = false }: Props) {
  const [previewItem, setPreviewItem] = useState<VideoFeedItem | null>(null);

  const rows = groupByDay(v.visibleItems);

  return (
    <div className="relative h-full w-full overflow-y-auto pac-scroll-stable">
      {/* IG-style flat gallery: edge-to-edge, zero gap, fixed 3 columns,
          square tiles. Day-headers preserved but visually muted so the
          grid disposition reads as a single uniform mosaic (Instagram /
          TikTok profile / YouTube Shorts grid idiom). */}
      <div className="pb-10">
        <DayGroups
          rows={rows}
          onOpen={setPreviewItem}
          onDelete={readOnly ? undefined : onRequestDelete}
          pendingDeletes={v.pendingDeletes}
          requestPoster={v.requestPoster}
          requestPreview={v.requestPreview}
        />
        <GrandTotalFooter rows={rows} />
      </div>

      {/* Lazy preview modal — same one the feed rail uses. */}
      {previewItem && (
        <VideoPreviewModal
          item={previewItem}
          requestDetail={v.requestDetail}
          requestPlayback={v.requestPlayback}
          onClose={() => setPreviewItem(null)}
        />
      )}
    </div>
  );
}

// ── Day section (header + responsive card grid) ──────────────────────────

function DayGroups({
  rows,
  onOpen,
  onDelete,
  pendingDeletes,
  requestPoster,
  requestPreview,
}: {
  rows: ReturnType<typeof groupByDay>;
  onOpen: (item: VideoFeedItem) => void;
  onDelete?: (item: VideoFeedItem) => void;
  pendingDeletes: ReadonlySet<string>;
  requestPoster: UseVideoGalleryReturn['requestPoster'];
  requestPreview: UseVideoGalleryReturn['requestPreview'];
}) {
  // Walk rows once and emit one <section> per day-header containing only
  // the items immediately following that header (until the next header or
  // grand-total). This keeps the day-grouping contract while still letting
  // each day's items participate in a CSS grid.
  const sections: React.ReactNode[] = [];
  let i = 0;
  while (i < rows.length) {
    const row = rows[i];
    if (row.kind !== 'header') { i++; continue; }

    const headerRow = row;
    const items: VideoFeedItem[] = [];
    let j = i + 1;
    while (j < rows.length && rows[j].kind === 'video') {
      // Safe cast: kind narrowed above.
      items.push((rows[j] as { kind: 'video'; item: VideoFeedItem }).item);
      j++;
    }
    sections.push(
      <section
        key={`grid-${headerRow.key}`}
        aria-label={`Videos from ${dayHeaderLabel(headerRow.dayStartMs)}`}
        className="mb-0"
      >
        <DayHeader
          label={dayHeaderLabel(headerRow.dayStartMs)}
          count={headerRow.count}
          totalBytes={headerRow.totalBytes}
          totalDurationMs={headerRow.totalDurationMs}
        />
        {/* Instagram-grade mosaic: fixed 3 cols, zero gap, square tiles.
            We do NOT scale columns up on wider viewports — IG and TikTok
            profile grids stay 3-col by design because the visual rhythm
            depends on the 3×N composition. The whole grid is constrained
            to a centred max-width so tiles don't blow up on 4K monitors. */}
        <div className="mx-auto max-w-[975px] grid grid-cols-3 gap-0">
          {items.map((it) => (
            <VideoCard
              key={it.video_id}
              item={it}
              onOpen={onOpen}
              onDelete={onDelete}
              deleting={pendingDeletes.has(it.video_id)}
              requestPoster={requestPoster}
              requestPreview={requestPreview}
              dense
            />
          ))}
        </div>
      </section>
    );
    i = j;
  }
  return <>{sections}</>;
}

// ── Sticky day header (label · count · totals) ───────────────────────────

function DayHeader({
  label,
  count,
  totalBytes,
  totalDurationMs,
}: {
  label: string;
  count: number;
  totalBytes: number;
  totalDurationMs: number;
}) {
  return (
    <header
      className="sticky top-0 z-10 px-3 sm:px-5 lg:px-7
                 py-1.5 backdrop-blur-md bg-black/70
                 border-b border-white/5
                 flex items-baseline gap-2"
    >
      <h2 className="text-[13px] font-semibold text-white/90 tracking-tight">
        {label}
      </h2>
      <span className="text-[11px] text-neutral-500 tabular-nums">
        {count} {count === 1 ? 'video' : 'videos'}
        {' · '}
        {formatBytes(totalBytes)}
        {totalDurationMs > 0 ? ` · ${formatDuration(totalDurationMs)}` : ''}
      </span>
    </header>
  );
}

// ── Grand-total footer ───────────────────────────────────────────────────

function GrandTotalFooter({ rows }: { rows: ReturnType<typeof groupByDay> }) {
  const total = rows.find((r) => r.kind === 'grand-total');
  if (!total || total.kind !== 'grand-total') return null;
  return (
    <footer
      className="mt-4 px-3 py-2 rounded-lg border border-white/5 bg-white/[0.02]
                 flex items-baseline gap-2 text-[11px] text-neutral-500 tabular-nums"
      aria-label="Total across all visible days"
    >
      <span className="text-neutral-300 font-semibold">Total</span>
      <span>
        {total.count} videos across {total.daysCovered} {total.daysCovered === 1 ? 'day' : 'days'}
        {' · '}
        {formatBytes(total.totalBytes)}
        {total.totalDurationMs > 0 ? ` · ${formatDuration(total.totalDurationMs)}` : ''}
      </span>
    </footer>
  );
}
